import { ChevronRightIcon, GlobeAltIcon } from '@heroicons/react/outline';
import { XIcon } from '@heroicons/react/solid';
import {
  Affix,
  Button,
  ConfigProvider,
  Drawer,
  Input,
  InputRef,
  ThemeConfig,
  Tooltip,
} from 'antd';
import { Avatar } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import AppStoreGetIcon from 'assets/Icons/AppStoreGetIcon';
import CrownOutlineIcon from 'assets/Icons/CrownOutlineIcon';
import PlayStoreGetIcon from 'assets/Icons/PlayStoreGetIcon';
import StarWithoutBg from 'assets/Icons/StarWithoutBg';
import TrendingOutlineIcon from 'assets/Icons/TrendingOutlineIcon';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import moment from 'moment';
import { Montserrat } from 'next/font/google';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BiChevronDown } from 'react-icons/bi';
import { useRecoilState, useRecoilValue } from 'recoil';
import { twMerge } from 'tailwind-merge';

import useTailwindBreakpoint from '@/hooks/useTailwindBreakpoint';

import RenderIf from '@/components/Common/RenderIf';
import Header from '@/components/Layout/Header/index';
import CompleteProfile from '@/components/Layout/ProductsHeader/CompleteProfile';
import HorizontalMenu from '@/components/Layout/ProductsHeader/HorizontalMenu';
import LogoutButton from '@/components/Layout/ProductsHeader/LogoutButton';
import PersonalityValue from '@/components/Layout/ProductsHeader/PersonalityValue';
import SubscribeButton from '@/components/Layout/ProductsHeader/SubscribeButton';
import ViewProfile from '@/components/Layout/ProductsHeader/ViewProfile';
// import SubscriptionHeader from '@/components/Layout/SubscriptionHeader';
import ZAIChatDrawer from '@/components/ZAIChatLayout/ZAIChatDrawer';

import { countryAtom, isSubscriptionDisabled } from '@/atoms/countryState';
import { aiModalOpenAtomGlobal } from '@/atoms/pdfStates/aiModalOpenAtom';
import { isSubscriptionActive } from '@/utils/index';

import AnimatedNavbar from './AnimatedNavLink';
import FloatingCartButton from './FlottingButton';
import ProductDropdown from './ProductDropdown';
import GetOurAppModal from '../GetAppComp';
import ButtonDefault, { ButtonVariants } from '../shared/basic/button';
import { ShouldRender } from '../shared/basic/ShouldRender';
import {
  formateErrorAppwriteObject,
  formateErrorObject,
} from '../shared/formateErrorObject';
import { getUserInitials } from '../shared/getInitials';
import { getUserName } from '../shared/getToken';
import { notify } from '../shared/notify';
import ForgotPasswordModal from '../V2/ForgotPasswordModal';
import LoginModal from '../V2/LoginModal';
import SignUpModal from '../V2/SignUpModal';
import VerifyEmailModal from '../VerifyEmailModal';
import { authModalControl, tokenUpdate } from '../../atoms/authModalControl';
import { chatCount } from '../../atoms/chatCount';
import { hasNewBookAtom } from '../../atoms/hasNewBook';
import { isBannerVisible } from '../../atoms/isBannerVisible';
import { localCart } from '../../atoms/LocalCart';
import { user } from '../../atoms/user';
import { verifyModal } from '../../atoms/verifyModal';
import appwriteService from '../../config/appwrite';
import { EventDetails, logEvent } from '../../hooks/useClickEventTracking';
import { checkHasNewProduct } from '../../services/Library/Books';
import { getRouteBanner, IRouteBanner } from '../../services/RouteBanner';
import { getUserProfile } from '../../services/user';
import { User } from '../../utils/interfaces/User';
import { PreviewSchool, ZebraLearnLogo } from '../../../assets';
import BusinessHeader from '../../../assets/Icons/BusinessHeader';
import ChatIcon from '../../../assets/Icons/ChatIcon';
import InfoCircleIcon from '../../../assets/Icons/InfoCircle';
import KeyIcon from '../../../assets/Icons/Key';
import LibraryIcon from '../../../assets/Icons/LibraryIcon';
import BookIcon from '../../../assets/Icons/NewHeaderIcons/BookIcon';
import ContactUs from '../../../assets/Icons/NewHeaderIcons/ContactUs';
import CourseIcon from '../../../assets/Icons/NewHeaderIcons/CourseIcon';
import LiveIcon from '../../../assets/Icons/NewHeaderIcons/LiveIcon';
import SchoolIcon from '../../../assets/Icons/NewHeaderIcons/SchoolIcon';
import PencilEdit from '../../../assets/Icons/PencilEdit';
import ProfileIcons from '../../../assets/Icons/ProfileIcons';
import RewardIcon from '../../../assets/Icons/RewardIcon';
import SearchIconSVG from '../../../assets/Icons/SearchIconSVG';
import ShoppingCart from '../../../assets/Icons/shopping-cart';

interface HeaderLinkProps {
  text: string | ReactNode;
  route?: string;
  setOpen: (value: boolean) => void;
  isDrawer?: boolean;
  notRender?: boolean;
  index?: number;
  open?: boolean;
  notHaveDropDown?: boolean;
}

export interface RenderAvatarProps {
  containerStyle?: CSSProperties;
  wrapperStyle?: string;
  width?: number;
  redirect?: boolean;
  avatarStyle?: {
    fontSize: string;
    border: string;
  };
  userName?: string;
  avatarSrc?: string;
  gradientBg?: string;
  setShowDropDown?: (value: boolean) => void;
  onClose?: () => void;
}

export const triggerSessionEvent = (
  start: Date,
  end: Date,
  userData: User,
  pathname: string,
) => {
  const duration = (end.getTime() - start.getTime()) / 1000;

  if (duration < 1) return;

  const eventParams: EventDetails = {
    event_name: 'screen_session',
    source: 'ask_ai',
    source_type: 'global',
    source_id: null,
    sub_source: null,
    sub_source_id: null,
    unit: 'seconds',
    value: duration,
    activity_start: moment(start).format('YYYY-MM-DD HH:mm:ss'),
    activity_end: moment(end).format('YYYY-MM-DD HH:mm:ss'),
    meta: { click_source: 'previous_screen' },
  };

  logEvent({
    eventDetails: eventParams,
    userData,
    pathname: pathname,
  });
};

const RenderAvatar = ({
  containerStyle,
  wrapperStyle,
  width,
  redirect,
  avatarStyle,
  userName,
  avatarSrc,
  gradientBg,
  setShowDropDown,
  onClose,
}: RenderAvatarProps) => {
  const router = useRouter();
  const avatarBackground = useMemo(
    () => (!avatarSrc ? gradientBg : undefined),
    [avatarSrc, gradientBg],
  );

  const avatarClass = useMemo(
    () => clsx(!avatarSrc ? 'bg-primary' : 'bg-bgColor'),
    [avatarSrc],
  );

  const shouldRenderEditIcon = useMemo(() => !!redirect, [redirect]);

  const handleClick = () => {
    if (redirect) {
      router.push('/profile');
      React.startTransition(() => {
        setShowDropDown && setShowDropDown(false);
        onClose && onClose();
      });
    }
  };

  return (
    <div
      onClick={handleClick}
      style={{ width, height: width, ...containerStyle }}
      className={clsx(wrapperStyle, 'relative')}
    >
      <Avatar
        size={width}
        alt='profile-picture'
        style={{
          ...avatarStyle,
          background: avatarBackground,
        }}
        className={avatarClass}
        src={avatarSrc}
      >
        {!avatarSrc && !userName ? (
          <ProfileIcons width='8' />
        ) : (
          userName && getUserInitials(userName)
        )}
      </Avatar>
      {shouldRenderEditIcon && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            borderRadius: '50%',
            cursor: 'pointer',
          }}
        >
          <div className='bg-[#121212] translate-y-1 rounded-full flex justify-center items-center'>
            <PencilEdit width='20' fillBgColor='#121212' />
          </div>
        </div>
      )}
    </div>
  );
};
export interface RenderMenuSectionProps {
  items: {
    text: string;
    customSVG?: ReactNode;
    onClick?: () => void;
    link?: string;
    notHaveDropDown?: boolean;
  }[];
  isCenter?: boolean;
}

export interface ILinks {
  text: string | ReactNode;
  route?: string;
  notRender?: boolean;
  type?: string;
  notHaveDropDown?: boolean;
}

interface ProductsHeaderProps {
  isShadow?: boolean;
  links: ILinks[];
  isLoggedIn?: boolean;
}

const config: ThemeConfig = {
  token: {
    colorPrimary: 'white',
  },
};

const montserrat = Montserrat({
  subsets: ['latin'],
  fallback: ['Segoe UI', 'Roboto', 'Arial', 'sans-serif'],
  display: 'swap',
});

export enum HeaderIcons {
  // HOME = 'Home',
  BOOKS = 'Books',
  COURSES = 'Courses',
  SCHOOLS = 'Schools',
  LIVE = 'Live',
  CONTACT_US = 'Contact Us',
  BUSINESS = 'For Business',
}

export const ICON_MAPPING = {
  [HeaderIcons.BOOKS]: <BookIcon />,
  [HeaderIcons.COURSES]: <CourseIcon />,
  [HeaderIcons.SCHOOLS]: <SchoolIcon />,
  [HeaderIcons.LIVE]: <LiveIcon />,
  [HeaderIcons.CONTACT_US]: <ContactUs />,
  [HeaderIcons.BUSINESS]: <BusinessHeader />,
};
export const HeaderLink: React.FC<HeaderLinkProps> = ({
  text,
  route,
  setOpen,
  isDrawer = false,
  notRender = false,
  index = 0,
  open = false,
  notHaveDropDown = false,
}) => {
  const router = useRouter();
  const screens = useBreakpoint();
  const isSelected =
    route &&
    !router.pathname.includes('/product') &&
    (router.pathname === route ||
      (router.pathname.includes(route) && route !== '/'));

  const isHome = text !== 'Home';

  const linkRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (isSelected && linkRef.current) {
      linkRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });

      setTimeout(() => {
        const offset = 100;
        const rect = linkRef.current?.getBoundingClientRect();
        if (rect) {
          const scrollPosition = rect.left - offset;
          window.scrollBy({ left: scrollPosition, behavior: 'smooth' });
        }
      }, 100);
    }
  }, [isSelected]);

  const content = (
    <div ref={linkRef} className='group w-full'>
      <div
        className={twMerge(
          clsx(
            ' flex items-center cursor-pointer border   w-full',
            !isHome ? 'p-1.5 lg:p-2 rounded-full ' : ' rounded-[10px]',
            'border-none ps-4 pe-2.5 xl:h-[40px] sm:h-[32px] h-[36px] bg-cartBgColor group-hover:bg-grayZl-900  text-textLightGray sm:rounded-[10px] rounded-[8px]',
          ),

          router.pathname === '/zai' || router.pathname.startsWith('/course/')
            ? 'bg-grayZl-900 group-hover:bg-grayZl-850'
            : '',
          !screens.xl ? 'ps-4 pe-4' : '',
          'transition-all sm:hover:scale-105 sm:active:scale-95 active:scale-95 sm:hover:translate-y-[1px]',
          notHaveDropDown ? 'pe-4' : '',
          isSelected
            ? 'border-none text-white bg-primary group-hover:bg-primary/95'
            : '',
        )}
      >
        {!isHome ? (
          <motion.div
            key='icon'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            {ICON_MAPPING[text as HeaderIcons]}
          </motion.div>
        ) : (
          <motion.div
            key='icon-text'
            className='flex items-center w-full gap-[2px] '
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <ShouldRender check={isHome}>
              <span className='text-sm/[22px] whitespace-nowrap w-full font-medium'>
                {text}
              </span>
            </ShouldRender>
            <ShouldRender check={screens.xl && !notHaveDropDown}>
              {/* <ChevronDownIcon className='w-4 h-4 font-medium transform transition-transform duration-300 group-hover:rotate-180' /> */}
              <BiChevronDown className='w-4 h-4 min-w-4 min-h-4 text-white' />
            </ShouldRender>
          </motion.div>
        )}
      </div>
    </div>
  );

  return (
    <ShouldRender check={!notRender}>
      <ShouldRender check={!isDrawer}>
        <AnimatePresence>
          <motion.div
            onClick={() => {
              if (!isDrawer) {
                route && router.push(route);
                setOpen && setOpen(false);
              }
            }}
            key={`${text}-${route}-${isSelected}`}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className={clsx(
              'flex h-full items-center hover:cursor-pointer w-full text-center',
              isSelected ? 'text-primary' : 'text-gray-600',
            )}
          >
            {text === 'SCHOOLS' ? (
              <Tooltip
                title={
                  <div
                    onClick={() => {
                      route && router.push(route);
                    }}
                    className='flex justify-center cursor-pointer'
                  >
                    <Image src={PreviewSchool} alt='AuthorLarge' width={270} />
                  </div>
                }
              >
                {content}
              </Tooltip>
            ) : (
              content
            )}
          </motion.div>
        </AnimatePresence>
      </ShouldRender>
      <ShouldRender check={isDrawer && open}>
        <motion.div
          onClick={() => {
            if (!isDrawer) {
              route && router.push(route);
              setOpen && setOpen(false);
            }
          }}
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{
            type: 'spring',
            stiffness: 260,
            damping: 20,
            delay: 0.1 + index / 10,
          }}
          className={clsx(
            'flex h-full items-center hover:cursor-pointer',
            isSelected ? 'text-primary' : 'text-gray-600',
          )}
        >
          {text === 'SCHOOLS' ? (
            <Tooltip
              title={
                <div
                  onClick={() => {
                    route && router.push(route);
                  }}
                  className='flex justify-center cursor-pointer'
                >
                  <Image src={PreviewSchool} alt='AuthorLarge' width={270} />
                </div>
              }
            >
              {content}
            </Tooltip>
          ) : (
            content
          )}
        </motion.div>
      </ShouldRender>
    </ShouldRender>
  );
};

const ProductsHeader: React.FC<ProductsHeaderProps> = ({
  isShadow = false,
  links,
}) => {
  const twScreen = useTailwindBreakpoint();
  const router = useRouter();
  const [showSearch, setShowSearch] = useState(false);
  const [userData, setUserData] = useRecoilState<User | undefined>(user);
  const [visible, setVisible] = useRecoilState(authModalControl);
  const [showDropDown, setShowDropDown] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState<string>();
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [userName, setUserName] = useState<string>();
  const [visibleGetAppModal, setVisibleGetAppModal] = useState(false);
  const [visibleVerifyEmailModal, setVisibleVerifyEmailModal] =
    useRecoilState(verifyModal);

  const [openDropDown, setOpenDropDown] = useState(false);
  const [countryState, setCountryState] = useRecoilState(countryAtom);

  const [isBannerVisibleValue, setIsBannerVisibleValue] = useRecoilState<
    boolean | null
  >(isBannerVisible);
  const [routeBanner, setRouteBanner] = useState<IRouteBanner | null>(null);
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const prevPathRef = useRef<string>(router.pathname);

  const BANNER_HIDE_KEY_PREFIX = 'routeBannerClosed_';

  const isRouteBannerHidden = (bannerId: string) => {
    if (typeof window === 'undefined' || !bannerId) return false;
    const expiry = localStorage.getItem(`${BANNER_HIDE_KEY_PREFIX}${bannerId}`);
    if (!expiry) return false;

    const expiryTime = Number(expiry);
    if (Number.isNaN(expiryTime)) {
      localStorage.removeItem(`${BANNER_HIDE_KEY_PREFIX}${bannerId}`);
      return false;
    }

    if (Date.now() > expiryTime) {
      localStorage.removeItem(`${BANNER_HIDE_KEY_PREFIX}${bannerId}`);
      return false;
    }

    return true;
  };

  const bannerRoutes = [
    '/',
    '/books',
    '/courses',
    '/schools',
    '/live',
    '/contact',
  ];

  const isMatchingRoute = useMemo(() => {
    return bannerRoutes.some(
      (bannerRoute) =>
        router.pathname === bannerRoute ||
        router.pathname === `${bannerRoute}/`,
    );
  }, [router.pathname, bannerRoutes]);

  const chatCountValue = useRecoilValue(chatCount);

  const [open, setOpen] = useState<boolean>(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [androidRedirectUrl, setAndroidRedirectUrl] = useState<string | null>(
    null,
  );
  const [openDrawerGlobal, setOpenDrawerGlobal] = useRecoilState<boolean>(
    aiModalOpenAtomGlobal,
  );

  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);
  const screens = useBreakpoint();
  const localCartCount = useRecoilValue(localCart);
  const inputRef = useRef<InputRef | null>(null);
  const isSchoolsPage = router.pathname.startsWith(
    '/schools/[schoolId]/[postId]',
  );
  const isCheckoutPage = !router.asPath.includes('/checkout');
  const isTrueCheckoutPage = router.asPath.includes('/checkout');
  const isCartPage = router.asPath.includes('/cart');
  const isPlatformCheckoutPage =
    router.asPath.includes('/platform-checkout') ||
    router.asPath.includes('/byob-checkout');
  const isVerified = useRef(false);
  const [hasNewBook, setHasNewBook] = useRecoilState<boolean>(hasNewBookAtom);
  const isPdf = router.asPath.startsWith('/pdf/');
  const [index] = useState(0);
  const validPaths = Object.freeze([
    '/',
    '/books/',
    '/books/shop',
    '/books/shop/',
    '/books/shop/[category]',
    '/courses/shop',
    '/courses/shop/',
    '/blog',
    '/blog/',
    '/blog/[category]',
    '/courses/shop/[category]',
    '/books/[bookId]',
    '/schools/[schoolId]',
    '/schools/',
    '/live/',
    '/courses/',
    '/about_us/',
    '/contact/',
    '/library/',
    '/broadcast-message/',
    '/checkout-v2',
  ]);

  const validPathSearch = [
    '/',
    '/books/',
    '/books',
    '/oranges',
    '/oranges/',
    '/bundles',
    '/books/shop',
    '/books/shop/[category]',
    '/courses/shop',
    '/books/shop/',
    '/courses/shop/',
    '/courses/shop/[category]',
    '/schools/',
    '/schools',
    '/live/',
    '/live',
    '/partnerships',
    '/courses',
    '/courses/[courses_slug]',
    '/books/[bookId]',
    '/contact',
    '/schools/[schoolId]',
    '/reward-store',
    '/broadcast-message',
    '/rewards',
    '/404',
    '/byob',
  ];

  const validPathSearchNav = [
    '/',
    '/books/',
    '/books',
    '/oranges',
    '/oranges/',
    '/books/shop',
    '/books/shop/[category]',
    '/books/shop/',
    '/courses/shop',
    '/courses/shop/',
    '/courses/shop/[category]',
    '/schools/',
    '/schools',
    '/live/',
    '/live',
    '/courses/',
    '/courses',
    '/contact/',
    '/contact',
  ];
  const invalidPathBorder = ['/schools/[schoolId]/[postId]'];
  const placeholders = ['Books', 'Courses', 'Schools', 'Live'];
  const keyWord = router?.query?.search || '';
  const environment = process.env.NEXT_PUBLIC_APP_ENV;
  const fullName = userData?.name || userName || '';
  const firstTwoWords = fullName.split(' ').slice(0, 2).join(' ');
  const displayName =
    firstTwoWords.length > 15
      ? `${firstTwoWords.slice(0, 20)}...`
      : firstTwoWords;
  const isShopPage =
    !router.pathname.includes('/books/shop') &&
    !router.pathname.includes('/courses/shop');
  const ZAI_SESSION_KEY = 'zai_screen_session';

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const avatarSrc =
    userData?.avatar &&
    `${process.env.NEXT_PUBLIC_IMAGE_BASE_URL_ECOM}/${userData?.avatar}`;

  const handleRemoveBanner = () => {
    setIsBannerVisibleValue(false);

    setTimeout(() => {
      setIsBannerVisibleValue(null);
    }, 2000);
  };
  const cleanedPath = router.pathname.trim();

  const shouldRenderLinks = validPaths.some((path) =>
    cleanedPath.startsWith(path),
  );

  const onCancelCloseModal = () => {
    setVisible((prevState) => ({
      ...prevState,
      signUp: false,
      subHeadingCopy: '',
      headingCopy: '',
    }));
  };
  const onCancelCloseLoginModal = () => {
    setVisible((prevState) => ({
      ...prevState,
      login: false,
      subHeadingCopy: '',
      headingCopy: '',
      emailExistsMessage: false,
    }));
  };

  const onCancelCloseForgotPasswordModal = () => {
    setVisible((prevState) => ({
      ...prevState,
      forgotPassword: false,
    }));
  };

  const openZaiDrawer = () => {
    // if (openDrawerGlobal) {
    //   closeZai();
    //   return;
    // }
    // setOpenDrawerGlobal(true);
    askAiEventLog();
    router.push('/zai');
    // if (router.query.zai !== '1') {
    //   router.replace(
    //     {
    //       pathname: router.pathname,
    //       query: { ...router.query, zai: '1' },
    //     },
    //     undefined,
    //     { shallow: true },
    //   );
    // }
  };

  const closeZai = () => {
    setOpenDrawerGlobal(false);
    if (typeof localStorage === 'undefined') return;

    const raw = localStorage.getItem(ZAI_SESSION_KEY);
    if (raw) {
      try {
        const { start } = JSON.parse(raw);

        if (start) {
          const startDate = new Date(start);
          const endDate = new Date();

          triggerSessionEvent(
            startDate,
            endDate,
            userData as User,
            router.pathname,
          );
        }
      } catch {
        // ignore
      } finally {
        localStorage.removeItem(ZAI_SESSION_KEY);
      }
    }

    const { zai, session, ...rest } = router.query;

    router.replace(
      {
        pathname: router.pathname,
        query: rest,
      },
      undefined,
      { shallow: true },
    );
  };

  useEffect(() => {
    if (!router.isReady) return;

    if (router.query.zai === '1' && !openDrawerGlobal) {
      setOpenDrawerGlobal(true);
    }

    if (!router.query.zai && openDrawerGlobal) {
      closeZai();
    }
  }, [router.isReady, router.query.zai]);

  useEffect(() => {
    const fetchLoggedInStatus = async () => {
      const loggedInStatus = await appwriteService.isLoggedIn();
      const user_name = await getUserName();
      user_name && setUserName(user_name);
      setLoggedIn(loggedInStatus);
    };
    fetchLoggedInStatus();
    if (isTokenChanged) {
      setIsTokenChanged(false);
    }
  }, [appwriteService.account, isTokenChanged]);

  useEffect(() => {
    if (showSearch && shouldRenderLinks && isCheckoutPage && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showSearch, shouldRenderLinks, isCheckoutPage]);

  const sendVerificationLink = async () => {
    try {
      await appwriteService.sendVerificationEmail();
      setVisibleVerifyEmailModal(true);
    } catch (error: any) {
      notify(formateErrorAppwriteObject(error));
    }
  };

  useEffect(() => {
    if (screens.md) {
      onClose();
    }
  }, [screens]);

  useEffect(() => {
    if (!router.isReady) return;
    setShowSearch(false);
    initialize();

    const prevPath = prevPathRef.current;
    const currentPath = router.pathname;

    if (prevPath !== currentPath && openDrawerGlobal) {
      closeZai();
    }

    prevPathRef.current = currentPath;
  }, [router.pathname]);

  useEffect(() => {
    const fetchRouteBanner = async () => {
      try {
        // Normalize route: send the pathname as-is, backend will handle it
        const response = await getRouteBanner(router.pathname);
        if (
          response.data &&
          response.data.isActive &&
          response.data.route.includes(router.pathname)
        ) {
          setRouteBanner(response.data);
          if (isRouteBannerHidden(response.data._id)) {
            setShowBanner(false);
          } else {
            setShowBanner(true);
          }
        } else {
          setRouteBanner(null);
          setShowBanner(false);
        }
      } catch (error) {
        setRouteBanner(null);
        setShowBanner(false);
      }
    };
    fetchRouteBanner();
  }, [router.pathname]);


  useEffect(() => {
    if (open) {
      const eventParams: EventDetails = {
        event_name: 'screen_view',
        source: 'global',
        source_type: 'menu',
        source_id: null,
        sub_source: null,
        sub_source_id: null,
        unit: 'boolean',
        value: 1,
      };

      logEvent({
        eventDetails: eventParams,
        userData: userData,
        pathname: router.pathname,
      });
    }
  }, [open])

  const renderMenuSection = ({
    items,
    isCenter = false,
  }: RenderMenuSectionProps) => (
    <div className={twMerge(montserrat.className, 'px-5')}>
      <div className='flex flex-col font-medium rounded-[10px] border border-grayZl-900 overflow-hidden gap-px bg-grayZl-900'>
        {items.map((item, index) => {
          const isActive = item.link
            ? router.pathname.includes(item.link)
            : false;

          return (
            <button
              key={`${item.text}-${index}`}
              onClick={() => {
                item.onClick && item.onClick();
                if (item.text !== 'More Settings') {
                  setShowDropDown(false);
                }
              }}
              className={`
                ${!isCenter ? 'justify-start gap-x-3.5' : 'justify-center gap-x-2'}
                 bg-[#000000] py-2.5 md:py-2.5 px-3
                text-textGrayColor hover:text-primary flex items-center
              `}
            >
              <div className='flex justify-between items-center w-full'>
                <span className='flex gap-x-3 items-center text-white'>
                  <div
                    className={clsx(
                      isActive ? 'text-white' : 'text-[#9F9EA6]',
                      'flex items-center',
                    )}
                  >
                    {item.customSVG}
                  </div>

                  <span className='text-xs/5 text-white font-medium'>
                    {item.text}
                  </span>
                </span>

                <div className='flex justify-center items-center gap-x-3'>
                  <ShouldRender
                    check={
                      (chatCountValue && item.text === 'Message') ||
                      (hasNewBook && item.text === 'Library')
                    }
                  >
                    <span className='w-1.5 h-1.5 rounded-full bg-primary'></span>
                  </ShouldRender>
                  <span className='flex justify-center items-center rounded-full'>
                    <ChevronRightIcon className='h-4 text-white' />
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  const gradientBg = 'linear-gradient(180deg, #FF5612 0%, #C72707 100%)';
  const getFlagUrl = (code: string) =>
    `https://flagcdn.com/w40/${code.toLowerCase()}.webp`;

  const onClose = () => {
    setOpen(false);
  };
  const dropdownMenu = (
    <div
      ref={dropdownRef}
      className={clsx(
        montserrat.className,
        'custom-dropdown-menu bg-grayZl-950  rounded-xl text-fadedWhite customScrollBar',
      )}
    >
      <div className='overflow-y-auto flex flex-col gap-4'>
        <ViewProfile
          userName={userName}
          avatarSrc={avatarSrc}
          gradientBg={gradientBg}
          RenderAvatar={RenderAvatar}
          setShowDropDown={setShowDropDown}
          onClose={onClose}
          setOpen={setOpen}
          displayName={displayName}
        />
        <ShouldRender check={!isSubscriptionDisabled()}>
          <SubscribeButton
            setShowDropDown={setShowDropDown}
            setOpen={setOpen}
          />
        </ShouldRender>
        <PersonalityValue onClose={onClose} setShowDropDown={setShowDropDown} />
        <CompleteProfile
          sendVerificationLink={sendVerificationLink}
          setShowDropDown={setShowDropDown}
          onClose={onClose}
        />

        <HorizontalMenu onClose={onClose} setShowDropDown={setShowDropDown} />
        {renderMenuSection({
          items: [
            ...(isSubscriptionActive(userData)
              ? [
                {
                  text: 'Subscription',
                  link: 'subscription',
                  customSVG: <CrownOutlineIcon width={20} height={20} />,
                  onClick: () => {
                    router.push('/subscription/');
                    onClose();
                  },
                },
              ]
              : []),
            {
              text: 'Library',
              link: 'library',
              customSVG: <LibraryIcon width={20} height={20} />,
              onClick: () => {
                router.push('/library/');
                onClose();
              },
            },
            {
              text: 'Cart',
              link: 'cart',
              customSVG: <ShoppingCart width={20} />,
              onClick: () => {
                router.push('/cart');
                onClose();
              },
            },
            {
              text: 'Rewards',
              link: 'reward-store',
              customSVG: <RewardIcon width={20} height={20} />,
              onClick: () => {
                router.push('/reward-store');
                onClose();
              },
            },
            {
              text: 'Bulk Orders',
              customSVG: <TrendingOutlineIcon width={20} height={20} />,
              onClick: () => {
                if (typeof window !== 'undefined') {
                  const url = 'https://go.zebralearn.com/inquiry-form';
                  window.open(url, '_blank');
                }
                onClose();
              },
            },
            {
              text: 'Message',
              link: 'broadcast-message',
              customSVG: <ChatIcon width={20} height={20} />,
              onClick: () => {
                router.push('/broadcast-message');
                onClose();
              },
            },
            {
              text: 'Country',
              customSVG: countryState.selectedCountry ? (
                <div className='relative w-5 h-4 overflow-hidden rounded-sm'>
                  <Image
                    src={getFlagUrl(countryState.selectedCountry.code)}
                    alt={countryState.selectedCountry.name}
                    fill
                    sizes='20px'
                    className='object-cover rounded-sm'
                    loading='lazy'
                  />
                </div>
              ) : (
                <GlobeAltIcon width={20} height={20} />
              ),
              onClick: () => {
                setCountryState((prev) => ({
                  ...prev,
                  isOpen: true,
                }));
                onClose();
              },
            },
          ],
        })}
        <LogoutButton setShowDropDown={setShowDropDown} />
      </div>
      {/* <ShouldRender check={screens.md}>
        <div className='border-b  border-dividerColor pt-2.5' />
      </ShouldRender> */}
    </div>
  );

  const showDrawer = () => {
    setOpen(true);
  };

  useEffect(() => {
    setSearchKeyword(keyWord as string);
  }, [keyWord]);

  const keyCount = useMemo(() => {
    const productFeature = userData?.features?.find(i => i?.name === 'products');
    return (
      (productFeature?.total_quantity || 0) - (productFeature?.used_quantity || 0)
    );
  }, [userData]);

  const verifyUserEmail = async () => {
    try {
      await appwriteService.verifyVerificationEmail(
        router.query.userId as string,
        router.query.secret as string,
      );
      const data = await getUserProfile();
      setUserData(data?.data);
      router.replace(router.pathname, undefined, { shallow: true });
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    }
  };

  useEffect(() => {
    if (!isVerified.current && router.query.userId && router.query.secret) {
      verifyUserEmail();
      isVerified.current = true;
    }
  }, [router.query.userId, router.query.secret]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchKeyword = e.target.value;
    setSearchKeyword(newSearchKeyword);
    Cookies.set('search', newSearchKeyword);

    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, search: newSearchKeyword },
      },
      undefined,
      { shallow: true },
    );
  };

  const askAiEventLog = () => {
    if (typeof localStorage === 'undefined') return;
    if (!localStorage.getItem(ZAI_SESSION_KEY)) {
      localStorage.setItem(
        ZAI_SESSION_KEY,
        JSON.stringify({
          start: new Date().toISOString(),
        }),
      );
    }

    const eventParams: EventDetails = {
      event_name: 'screen_view',
      source: 'ask_ai',
      source_type: 'global',
      source_id: null,
      sub_source: null,
      sub_source_id: null,
      unit: 'boolean',
      value: 1,
      meta: { click_source: 'previous_screen' },
    };

    logEvent({
      eventDetails: eventParams,
      userData,
      pathname: router.pathname,
    });
  };

  const useAppButton = (
    <div className={montserrat.className}>
      <Button
        onClick={() => {
          if (typeof window !== 'undefined') {
            window.open('https://zbrlrn.com/download-zl-app');
          }
          const eventParams: EventDetails = {
            event_name: 'download_app',
            source: 'global',
            source_type: 'global',
            source_id: null,
            sub_source: null,
            sub_source_id: null,
            unit: 'boolean',
            value: 1,
            meta: {
              click_source: 'app_popup',
            },
          };

          logEvent({
            eventDetails: eventParams,
            userData: userData,
            pathname: router.pathname,
          });
          if (typeof mixpanel !== 'undefined' && environment === 'production') {
            mixpanel.track(eventParams.event_name as string, eventParams);
          }
        }}
        className='border-primary border-1 bg-transparent border-opacity-50 md:border-[2px] text-[10px] xxxs:text-xs md:text-base px-2 md:px-4 hover:text-white hover:border-[#737373] text-primary h-7 xs:h-8 md:h-[38px] rounded-lg  font-semibold'
      >
        Use App
      </Button>
    </div>
  );

  const desktopUseApp = (
    <div className={montserrat.className}>
      <ButtonDefault
        size={0}
        variant={ButtonVariants.UNSTYLED}
        className=' border border-primary text-xs text-primary font-semibold py-[5px] px-2.5 rounded'
        onClick={() => {
          if (typeof window !== 'undefined') {
            window.open('https://zbrlrn.com/download-zl-app');
          }
          const eventParams: EventDetails = {
            event_name: 'download_app',
            source: 'global',
            source_type: 'global',
            source_id: null,
            sub_source: null,
            sub_source_id: null,
            unit: 'boolean',
            value: 1,
            meta: {
              click_source: 'app_popup',
            },
          };

          logEvent({
            eventDetails: eventParams,
            userData: userData,
            pathname: router.pathname,
          });
          if (typeof mixpanel !== 'undefined' && environment === 'production') {
            mixpanel.track(eventParams.event_name as string, eventParams);
          }
        }}
      >
        Use App
      </ButtonDefault>
    </div>
  );
  const initialize = async () => {
    const cookieValue = Cookies.get('hasNewBookInLibrary');

    if (cookieValue !== undefined) {
      setHasNewBook(cookieValue === 'true');
      return;
    }

    try {
      const { data } = await checkHasNewProduct();

      const hasNew = data?.hasNewProduct === true;

      Cookies.set('hasNewBookInLibrary', hasNew.toString(), { expires: 1 });
      setHasNewBook(hasNew);
    } catch (error) {
      console.error('Failed to check for new product:', error);

      Cookies.set('hasNewBookInLibrary', 'false', { expires: 0.05 });
      setHasNewBook(false);
    }
  };
  const ZaiButton = () => {
    return (
      <div className='relative inline-flex rounded-[10px] p-[1px] overflow-hidden cursor-pointer'>
        <div className='absolute inset-[-150%] spin-smooth'>
          <div
            className='absolute inset-0'
            style={{
              background:
                'conic-gradient(from 37deg at 50% 50%, #51A0F7, #7328F2, #7E0D9D, #51A0F7)',
            }}
          />
        </div>

        <div className='relative z-10 flex items-center gap-2 rounded-[9px] bg-black px-3 md:px-4 py-[6px] md:py-[4px] lg:py-[9px] text-sm font-medium text-white'>
          <StarWithoutBg width='14' />
          <span>zAI</span>
        </div>
      </div>
    );
  };
  const isSearch =
    !router.asPath.includes('/policies') &&
    !showSearch &&
    // isShopPage &&
    validPathSearch.includes(router.pathname) &&
    !openDrawerGlobal;

  const ShortHeaderInPhoneRoute = ['/courses/[courses_slug]'];

  const shortHeaderInPhone =
    !twScreen.sm && ShortHeaderInPhoneRoute.includes(router.pathname);

  const mobileBanner = (
    <motion.div
      className={clsx(
        openDrawerGlobal ? 'z-[2000]' : 'z-40',
        'bg-cartBgColor flex justify-between items-center h-16 px-4  -translate-y-2 w-full',
      )}
      initial={{ x: 0 }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ duration: 0.3 }}
    >
      <div className='flex justify-between w-full items-center '>
        <div className='flex gap-x-4 items-center justify-start'>
          <button
            type='button'
            className='px-0 flex items-center rounded-full'
            onClick={handleRemoveBanner}
            aria-label='Close banner'
          >
            <XIcon className='w-5 h-5' />
          </button>
          <div className='flex flex-col justify-center'>
            <span className='font-semibold text-sm'>ZebraLearn App</span>
            <span className='text-xs font-medium text-textLightGray'>
              Install for a better experience
            </span>
          </div>
        </div>
        <div className='mt-1'>{useAppButton}</div>
      </div>
    </motion.div>
  );
  const desktopBanner = (
    <motion.div
      onClick={() => {
        if (typeof window !== 'undefined') {
          window.open('https://zbrlrn.com/download-zl-app');
        }
        const eventParams: EventDetails = {
          event_name: 'download_app',
          source: 'global',
          source_type: 'global',
          source_id: null,
          sub_source: null,
          sub_source_id: null,
          unit: 'boolean',
          value: 1,
          meta: {
            click_source: 'app_popup',
          },
        };

        logEvent({
          eventDetails: eventParams,
          userData: userData,
          pathname: router.pathname,
        });
        if (typeof mixpanel !== 'undefined' && environment === 'production') {
          mixpanel.track(eventParams.event_name as string, eventParams);
        }
      }}
      className='bg-cartBgColor flex justify-between items-center h-12 px-4 w-full z-50'
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -100, opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className='flex justify-between w-full z-50 items-center'>
        <div className='flex gap-x-4 items-center justify-center w-full'>
          <span className='text-sm text-white font-light flex items-center gap-x-3.5'>
            Download the ZebraLearn app for seamless learning on the go{' '}
            {desktopUseApp}
          </span>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleRemoveBanner();
          }}
          className='bg-[#0000004D] rounded-full p-1.5 text-white flex justify-center items-center'
        >
          <XIcon className='w-4 h-4' />
        </button>
      </div>
    </motion.div>
  );

  const isZai = router.pathname === '/zai';

  const shouldRenderBanner =
    isBannerVisibleValue === true &&
    (validPathSearchNav.includes(router.pathname) ||
      router.asPath.includes('/pdf'));

  useEffect(() => {
    if (screens.md) {
      setIsBannerVisibleValue(true);
    }
  }, [screens]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);

    const defaultParams: Record<string, string> = {
      utm_source: 'zebralearn',
      utm_medium: 'website',
      utm_campaign: 'download_app',
    };

    const finalParams = new URLSearchParams();

    urlParams.forEach((value, key) => {
      finalParams.set(key, value);
    });

    Object.entries(defaultParams).forEach(([key, value]) => {
      if (!finalParams.has(key)) {
        finalParams.set(key, value);
      }
    });

    const encodedReferrer = encodeURIComponent(finalParams.toString());
    const androidUrl = `https://play.google.com/store/apps/details?id=com.zebralearn.reader&referrer=${encodedReferrer}`;

    setAndroidRedirectUrl(androidUrl);
  }, []);

  const isPdfPage = router.asPath.includes('/pdf') || router.pathname.includes('/course/');
  const isDesktop = true;
  const showBorder = !invalidPathBorder.includes(router.pathname);

  return (
    <>
      <ConfigProvider theme={config}>
        <ShouldRender check={shouldRenderBanner && !isDesktop}>
          <AnimatePresence mode='wait'>
            <div
              className={clsx(
                openDrawerGlobal ? 'z-[2000]' : 'z-40',
                'relative',
              )}
            >
              {isPdfPage ? (
                isDesktop ? (
                  desktopBanner
                ) : (
                  mobileBanner
                )
              ) : (
                <Affix offsetTop={0} style={{ zIndex: 9999 }}>
                  {isDesktop ? desktopBanner : mobileBanner}
                </Affix>
              )}
            </div>
          </AnimatePresence>
        </ShouldRender>
        <div className='flex flex-col z-50'>
          <Header
            setShowBanner={setShowBanner}
            routeBanner={routeBanner}
            showBanner={showBanner}
            shortHeaderInPhone={shortHeaderInPhone}
            showBorder={showBorder}
            isTrueCheckoutPage={isTrueCheckoutPage}
            isSearch={isSearch}
            isZai={isZai}
            isShadow={isShadow}
            isDesktop={isDesktop}
            validPathSearchNav={validPathSearchNav}
            isCartPage={isCartPage}
            isBannerVisibleValue={isBannerVisibleValue}
            isPdf={isPdf}
            isSchoolsPage={isSchoolsPage}
            isMatchingRoute={isMatchingRoute}
            isPlatformCheckoutPage={isPlatformCheckoutPage}
            isCheckoutPage={isCheckoutPage}
            userName={userName}
            dropdownRef={dropdownRef}
            showDrawer={showDrawer}
            setShowDropDown={setShowDropDown}
            showDropDown={showDropDown}
            dropdownMenu={dropdownMenu}
            hasNewBook={hasNewBook}
            chatCountValue={chatCountValue}
            isShopPage={isShopPage}
            showSearch={showSearch}
            validPathSearch={validPathSearch}
            loggedIn={loggedIn}
            keyCount={keyCount}
            setShowSearch={setShowSearch}
            setSearchKeyword={setSearchKeyword}
            handleInputChange={handleInputChange}
            searchKeyword={searchKeyword}
            hoveredIndex={hoveredIndex}
            setOpen={setOpen}
            openDropDown={openDropDown}
            openZaiDrawer={openZaiDrawer}
            setOpenDropDown={setOpenDropDown}
            setHoveredIndex={setHoveredIndex}
            timeoutIdRef={timeoutIdRef}
            links={links}
            shouldRenderLinks={shouldRenderLinks}
          />

          {/* <ShouldRender check={screens.lg}>
            <SubscriptionHeader isMatchingRoute={isMatchingRoute} />
          </ShouldRender> */}
          <RenderIf condition={false}>
            <ShouldRender
              check={
                validPathSearchNav.some((path) => path === router.pathname) &&
                !screens.lg
              }
            >
              <motion.div
                className={clsx(
                  isBannerVisibleValue
                    ? 'md:translate-y-8 lg:translate-y-10 mt-[30px]'
                    : 'md:translate-y-8 lg:translate-y-10 mt-10',
                  'flex bg-bgColor justify-between gap-1 z-0 md:gap-2 items-center pb-1 md:pb-2 md:mt-3 pt-3 px-4 md:px-5 lg:px-12',
                  '',
                )}
              >
                <ShouldRender
                  check={
                    router.asPath !== '/contact/' &&
                    !router.asPath.includes('/broadcast-message/') &&
                    !router.pathname.includes('/books/[bookId]') &&
                    validPathSearch.includes(router.pathname) &&
                    !screens.lg &&
                    isShopPage
                  }
                >
                  <div
                    className='flex justify-center items-center w-full'
                    style={{ minHeight: '48px' }}
                  >
                    {router.isReady ? (
                      <div className='relative w-full'>
                        <Input
                          size='small'
                          ref={inputRef}
                          value={searchKeyword}
                          onChange={handleInputChange}
                          className='text-sm rounded-full w-full  border-[1.5px]   placeholder:text-textDarkGrayColor py-1 px-3 text-white h-9 lg:h-10 bg-bgColor customInputBlack makeBgBlack  border-grayZl-900
                        focus:border-gray-600 focus:ring-0 '
                          style={{
                            backgroundColor: '#000000',
                          }}
                          prefix={
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.2 }}
                            >
                              <SearchIconSVG width={screens.lg ? '20' : '16'} />
                            </motion.div>
                          }
                          suffix={
                            searchKeyword?.length && (
                              <motion.div
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                <XIcon
                                  onClick={() => {
                                    Cookies.set('search', '');
                                    setSearchKeyword('');
                                    const { search, ...restQuery } =
                                      router.query;
                                    router.push(
                                      {
                                        pathname: router.pathname,
                                        query: restQuery,
                                      },
                                      undefined,
                                      { shallow: true },
                                    );
                                    setShowSearch(false);
                                  }}
                                  className='w-5 h-5 pr-1 text-[#A6A6A6] cursor-pointer'
                                />
                              </motion.div>
                            )
                          }
                        />

                        {!searchKeyword?.trim() && (
                          <div className='absolute font-medium left-10 top-1/2 -translate-y-1/2 whitespace-nowrap pointer-events-none text-textDarkGrayColor flex text-xs xxxs:text-sm'>
                            <span>Search for&nbsp;</span>"{' '}
                            <AnimatePresence mode='wait'>
                              <motion.span
                                key={index}
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -6 }}
                                transition={{ duration: 0.4 }}
                                className='italic'
                              >
                                {placeholders[index]}
                              </motion.span>
                            </AnimatePresence>
                            <p className='pl-1'> "</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className='bg-cartBgColor h-10 w-full lg:max-w-[195px] rounded-full animate-pulse' />
                    )}
                  </div>
                </ShouldRender>
                <ShouldRender check={isShopPage && !openDrawerGlobal}>
                  <motion.div
                    key='navbar'
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                  >
                    <AnimatedNavbar />
                  </motion.div>
                </ShouldRender>
              </motion.div>

              <ShouldRender check={!screens.md}>
                <Affix offsetTop={isBannerVisibleValue ? 100 : 54}>
                  <span className=' flex  bg-bgColor lg:justify-between gap-1 md:gap-2 overflow-x-auto no-scrollbar items-center z-10 pb-2 pt-1 md:pb-4 md:mt-4  px-4 md:px-5 lg:px-12'>
                    <ShouldRender
                      check={
                        shouldRenderLinks &&
                        isCheckoutPage &&
                        !isCartPage &&
                        !isPlatformCheckoutPage
                      }
                    >
                      {links.map((link, index) => {
                        const isZai = link.type === 'zai';

                        return (
                          <div
                            key={`${link.route}-${index}`}
                            className='relative inline-block w-fit'
                            onClick={() => {
                              if (isZai) {
                                openZaiDrawer();
                                return;
                              }
                            }}
                          >
                            {isZai ? (
                              <ZaiButton />
                            ) : (
                              <HeaderLink
                                text={link.text}
                                index={index}
                                open={openDropDown}
                                route={link.route}
                                notHaveDropDown={link?.notHaveDropDown || false}
                                setOpen={setOpen}
                              />
                            )}
                            {!isZai &&
                              openDropDown &&
                              hoveredIndex === index &&
                              link.type && (
                                <div className='absolute left-0 top-full z-50'>
                                  <ProductDropdown
                                    open={openDropDown}
                                    setOpen={setOpenDropDown}
                                    type={link.type}
                                  />
                                </div>
                              )}
                          </div>
                        );
                      })}
                    </ShouldRender>
                  </span>
                </Affix>
                {/* <SubscriptionHeader isMatchingRoute={isMatchingRoute} /> */}
              </ShouldRender>
            </ShouldRender>
          </RenderIf>
        </div>
      </ConfigProvider>

      <Drawer
        className='drawerPaddingNone drawerPaddingNoScrollBar  h-full bg-grayZl-950'
        width={450}
        height='100%'
        classNames={{
          header: 'bg-black p-4 flex item border-0',
        }}
        title={
          <div className='flex items-center justify-between '>
            <span
              onClick={() => router.push('/')}
              className='cursor-pointer flex  md:pl-0 justify-start w-2/5 h-5'
            >
              <span>
                <Image
                  src={ZebraLearnLogo}
                  alt='Logo'
                  className='object-contain w-full h-full max-w-36'
                />
              </span>
            </span>
            <span
              className='flex justify-end'
              onClick={() => {
                onClose();
              }}
            >
              <XIcon className='text-fadedWhite w-4 h-4' />
            </span>
          </div>
        }
        placement='right'
        closeIcon={false}
        onClose={onClose}
        open={open}
      >
        <div className={montserrat.className}>
          <div className='overflow-y-auto flex flex-col gap-4 '>
            <ViewProfile
              className='p-4'
              userName={userName}
              avatarSrc={avatarSrc}
              gradientBg={gradientBg}
              RenderAvatar={RenderAvatar}
              setShowDropDown={setShowDropDown}
              onClose={onClose}
              setOpen={setOpen}
              displayName={displayName}
            />
            <SubscribeButton
              setShowDropDown={setShowDropDown}
              setOpen={setOpen}
            />
            <PersonalityValue
              onClose={onClose}
              setShowDropDown={setShowDropDown}
            />
            <CompleteProfile
              sendVerificationLink={sendVerificationLink}
              setShowDropDown={setShowDropDown}
              onClose={onClose}
            />
            {/* <ReferEarn onClose={onClose} setShowDropDown={setShowDropDown} /> */}
            <HorizontalMenu
              onClose={onClose}
              setShowDropDown={setShowDropDown}
            />
            {renderMenuSection({
              items: [
                ...(isSubscriptionActive(userData)
                  ? [
                    {
                      text: 'Subscription',
                      link: 'subscription',
                      customSVG: <CrownOutlineIcon width={20} height={20} />,
                      onClick: () => {
                        router.push('/subscription/');
                        onClose();
                      },
                    },
                  ]
                  : []),
                {
                  text: 'Library',
                  link: 'library',
                  customSVG: <LibraryIcon width={20} height={20} />,
                  onClick: () => {
                    router.push('/library/');
                    onClose();
                  },
                },
                {
                  text: 'Cart',
                  link: 'cart',
                  customSVG: <ShoppingCart width={20} />,
                  onClick: () => {
                    router.push('/cart');
                    onClose();
                  },
                },
                {
                  text: 'Rewards',
                  link: 'reward-store',
                  customSVG: <RewardIcon width={20} height={20} />,
                  onClick: () => {
                    router.push('/reward-store');
                    onClose();
                  },
                },
                {
                  text: 'Bulk Orders',
                  customSVG: <TrendingOutlineIcon width={20} height={20} />,
                  onClick: () => {
                    if (typeof window !== 'undefined') {
                      const url =
                        'https://go.zebralearn.com/inquiry-form';
                      window.open(url, '_blank');
                    }
                    onClose();
                  },
                },
                {
                  text: 'Message',
                  link: 'broadcast-message',
                  customSVG: <ChatIcon width={20} height={20} />,
                  onClick: () => {
                    router.push('/broadcast-message');
                    onClose();
                  },
                },
                {
                  text: 'Country',
                  customSVG: countryState.selectedCountry ? (
                    <div className='relative w-5 h-4 overflow-hidden rounded-sm'>
                      <Image
                        src={getFlagUrl(countryState.selectedCountry.code)}
                        alt={countryState.selectedCountry.name}
                        fill
                        sizes='20px'
                        className='object-cover rounded-sm'
                        loading='lazy'
                      />
                    </div>
                  ) : (
                    <GlobeAltIcon width={20} height={20} />
                  ),
                  onClick: () => {
                    setCountryState((prev) => ({
                      ...prev,
                      isOpen: true,
                    }));
                    onClose();
                  },
                },
                {
                  text: 'Security & Passwords',
                  customSVG: <KeyIcon />,
                  onClick: () => {
                    router.push('/profile/?activeTab=2');
                    onClose();
                  },
                },
                {
                  text: 'Help & Support',
                  customSVG: <InfoCircleIcon width='20' />,
                  onClick: () => {
                    if (window !== undefined) {
                      window.open(
                        'https://support.zebralearn.com/support/home',
                        '_blank',
                      );
                    }
                    onClose();
                  },
                },
              ],
            })}
            <LogoutButton className='pb-0' setShowDropDown={setShowDropDown} />
          </div>
          <div className='flex flex-col gap-3 items-center px-4 py-8'>
            <p className='text-sm leading-[22px] font-medium text-white'>
              Download Our App
            </p>
            <div className='flex items-center gap-2 flex-wrap justify-center'>
              <PlayStoreGetIcon
                onClick={() => {
                  if (typeof window !== undefined) {
                    window.open(androidRedirectUrl as string, '_blank');
                  }
                }}
              />
              <AppStoreGetIcon
                onClick={() => {
                  if (typeof window !== undefined) {
                    window.open(
                      'https://apps.apple.com/in/app/zebralearn/id6477746268?utm_source=zebralearn_website',
                      '_blank',
                    );
                  }
                }}
              />
            </div>
          </div>
        </div>
      </Drawer>
      <ShouldRender
        check={
          localCartCount &&
          !isCartPage &&
          !isPlatformCheckoutPage &&
          isCheckoutPage &&
          router.pathname !== '/broadcast-message' &&
          !router.pathname.includes('/books/[bookId]') &&
          !router.pathname.includes('/courses/[courses_slug]') &&
          !router.pathname.includes('/reader-personality-test') &&
          !router.pathname.includes('/[watchId]') &&
          !isPdf
        }
      >
        <FloatingCartButton localCartCount={localCartCount} />
      </ShouldRender>
      <SignUpModal
        onCancel={onCancelCloseModal}
        visible={visible.signUp}
        noRedirect={visible?.noRedirect}
      />
      <LoginModal onCancel={onCancelCloseLoginModal} visible={visible.login} />
      <ForgotPasswordModal
        onCancel={onCancelCloseForgotPasswordModal}
        visible={visible.forgotPassword}
      />
      <GetOurAppModal
        visible={visibleGetAppModal}
        onCancel={() => setVisibleGetAppModal(false)}
        isClosable={true}
      />

      <VerifyEmailModal
        visible={visibleVerifyEmailModal}
        onCancel={() => setVisibleVerifyEmailModal(false)}
      />
      <ZAIChatDrawer
        onClose={() => {
          closeZai();
        }}
        open={openDrawerGlobal}
        initialSessionId={router.query.session as string | undefined}
      />
    </>
  );
};

export default ProductsHeader;
